import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { settings } from "../../config/settings";
import { authAPI } from "../../services/api";
import {
  AUTH_ERROR_CODES,
} from "../../utils/authErrors";
import { clearReferenceData, loadAllReferenceData } from "./referenceDataSlice";
import { applyUserSiteStyle } from "./themeSlice";

// Async thunk for login
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ email, password }, { rejectWithValue, dispatch }) => {
    try {
      // Real API call to backend
      const response = await authAPI.login({ email, password });

      if (response.user?.site_style) {
        dispatch(applyUserSiteStyle(response.user.site_style));
      }

      // Load reference data after successful login
      try {
        await dispatch(loadAllReferenceData()).unwrap();
      } catch (refDataError) {
        console.warn("Failed to load reference data:", refDataError);
        // Don't fail login if reference data fails to load
      }

      return response;
    } catch (error) {
      if (error.code === AUTH_ERROR_CODES.USER_INACTIVE) {
        return rejectWithValue(AUTH_ERROR_CODES.USER_INACTIVE);
      }
      return rejectWithValue(AUTH_ERROR_CODES.INVALID_CREDENTIALS);
    }
  }
);

// Async thunk for logout
export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(clearReferenceData());
    try {
      await authAPI.logout();
      return true;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Check if user data exists in localStorage
const getStoredUser = () => {
  try {
    const storedUser = localStorage.getItem(settings.STORAGE_KEYS.USER_DATA);
    return storedUser ? JSON.parse(storedUser) : null;
  } catch {
    return null;
  }
};

// Check if permissions exist in localStorage
const getStoredPermissions = () => {
  try {
    const storedPermissions = localStorage.getItem(
      settings.STORAGE_KEYS.USER_PERMISSIONS
    );
    return storedPermissions ? JSON.parse(storedPermissions) : [];
  } catch {
    return [];
  }
};

const storedUser = getStoredUser();
const storedToken = localStorage.getItem(settings.STORAGE_KEYS.AUTH_TOKEN);

const initialState = {
  user: storedUser,
  isAuthenticated: !!(storedToken && storedUser),
  isLoading: false,
  error: null,
  permissions: getStoredPermissions(),
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      if (action.payload) {
        localStorage.setItem(
          settings.STORAGE_KEYS.USER_DATA,
          JSON.stringify(action.payload)
        );
      }
    },
    updatePermissions: (state, action) => {
      state.permissions = action.payload || [];
      localStorage.setItem(
        settings.STORAGE_KEYS.USER_PERMISSIONS,
        JSON.stringify(state.permissions)
      );
    },
    updateAuthProfile: (state, action) => {
      if (action.payload.user) {
        state.user = action.payload.user;
        localStorage.setItem(
          settings.STORAGE_KEYS.USER_DATA,
          JSON.stringify(action.payload.user)
        );
      }
      if (action.payload.permissions) {
        state.permissions = action.payload.permissions;
        localStorage.setItem(
          settings.STORAGE_KEYS.USER_PERMISSIONS,
          JSON.stringify(action.payload.permissions)
        );
      }
    },
    clearUser: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.permissions = [];
      // Clear all auth data from localStorage
      localStorage.removeItem(settings.STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(settings.STORAGE_KEYS.USER_DATA);
      localStorage.removeItem(settings.STORAGE_KEYS.USER_PERMISSIONS);
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.permissions = action.payload.permissions || [];
        state.error = null;
        // Store user data in localStorage for persistence (using session-based auth)
        if (action.payload.user) {
          localStorage.setItem(
            settings.STORAGE_KEYS.USER_DATA,
            JSON.stringify(action.payload.user)
          );
        }
        if (action.payload.permissions) {
          localStorage.setItem(
            settings.STORAGE_KEYS.USER_PERMISSIONS,
            JSON.stringify(action.payload.permissions)
          );
        }
        // Store a session indicator instead of token
        localStorage.setItem(
          settings.STORAGE_KEYS.AUTH_TOKEN,
          "session-authenticated"
        );
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
        state.permissions = [];
        // Clear any invalid auth data
        localStorage.removeItem(settings.STORAGE_KEYS.AUTH_TOKEN);
        localStorage.removeItem(settings.STORAGE_KEYS.USER_DATA);
        localStorage.removeItem(settings.STORAGE_KEYS.USER_PERMISSIONS);
      })
      // Logout cases
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
        state.user = null;
        state.isAuthenticated = false;
        state.permissions = [];
        state.error = null;
        localStorage.removeItem(settings.STORAGE_KEYS.AUTH_TOKEN);
        localStorage.removeItem(settings.STORAGE_KEYS.USER_DATA);
        localStorage.removeItem(settings.STORAGE_KEYS.USER_PERMISSIONS);
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.permissions = [];
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        // Force logout even if API call failed
        state.user = null;
        state.isAuthenticated = false;
        state.permissions = [];
        localStorage.removeItem(settings.STORAGE_KEYS.AUTH_TOKEN);
        localStorage.removeItem(settings.STORAGE_KEYS.USER_DATA);
        localStorage.removeItem(settings.STORAGE_KEYS.USER_PERMISSIONS);
      });
  },
});

export const { clearError, setUser, clearUser, updatePermissions, updateAuthProfile } =
  authSlice.actions;
export default authSlice.reducer;
